import {Component, inject, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable, Subject, of} from 'rxjs';
import {debounceTime, switchMap, startWith, catchError} from 'rxjs/operators';
import {CommissionReconciliationService} from '../../service/commission-reconciliation.service';
import {TramsDataSourceService} from '../../service/trams-data-source.service';
import {TramsDataSourceModel} from '../../models/trams-data-source';
import {AdxBookingService} from 'src/app/adx-booking/service/adx-booking.service';
import {AdxBookingResponse} from 'src/app/adx-booking/model/adx-booking-response';
import {CommissionProviderFmsBooking} from '../../models/commission-reconciliation';
import {MatSnackBar} from '@angular/material/snack-bar';

@Component({
    selector: 'app-commission-reconciliation-edit-record',
    templateUrl: './commission-reconciliation-edit-record.component.html',
    standalone: false
})

export class CommissionReconciliationEditRecordComponent implements OnInit {
  commReconEditRecordForm!: FormGroup;
  dataSources: TramsDataSourceModel[] = [];
  bookingOptions$: Observable<AdxBookingResponse[]> = of([]);
  private search$ = new Subject<string>();
  isLoadingSpinner: boolean = false;
  loadingBookings = false;
  searchStarted = false;
  searchingError = '';
  private dataSourceService = inject(TramsDataSourceService);
  private adxBookingService = inject(AdxBookingService);
  private snackBar = inject(MatSnackBar);
  private commissionReconciliationService = inject(CommissionReconciliationService);

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<CommissionReconciliationEditRecordComponent, { mapped: boolean }>,
    @Inject(MAT_DIALOG_DATA) public data: CommissionProviderFmsBooking
  ) {}

  ngOnInit(): void {
    this.commReconEditRecordForm = this.fb.group({
      dataSourceId: [null, Validators.required],
      adxBookingId: [null, Validators.required]
    });
    
    this.dataSourceService.getTramsDataSourceList().subscribe(ds => {
      this.dataSources = ds;
      if (!this.commReconEditRecordForm.get('dataSourceId')!.value && ds.length) {
        this.commReconEditRecordForm.get('dataSourceId')!.setValue(ds[0].id);
        this.search$.next('');
      }
    });
    
    this.bookingOptions$ = this.search$.pipe(
      debounceTime(300),
      startWith(''),
      switchMap(term => {
        const sid = this.commReconEditRecordForm.get('dataSourceId')!.value;
        if (!sid) return of([]);
        this.loadingBookings = true;
        this.searchStarted = true;
        return this.adxBookingService.searchAdxBookings(sid, term).pipe(
          catchError(err => {
            console.error(err);
            this.searchingError = 'Search failed';
            return of([]);
          })
        );
      }),
    );

    this.bookingOptions$.subscribe(
      _ => this.loadingBookings = false
    );
  }

  displayBooking(booking: AdxBookingResponse): string {
    if (!booking) return '';
    return booking.vendor?.vendorName
            ? `${booking.id} - ${booking.vendor.vendorName}`
            : `${booking.id}`;
  }

  onDataSourceChange(selectedId: number): void {
    const bookingControl = this.commReconEditRecordForm.get('adxBookingId');

    bookingControl?.reset();

    if (selectedId) {
      bookingControl?.enable();
    } else {
      bookingControl?.disable();
    }
    this.search$.next('');
  }

  onSearchChange(value: string) { 
    this.search$.next(value);
  }

  OnCancel() {
    this.dialogRef.close({ mapped: false });
  }

  OnSubmit() {
    if (this.commReconEditRecordForm.invalid) return;
    this.isLoadingSpinner = true;
    const payload: CommissionProviderFmsBooking = {
      ccProcessorId: Number(this.data.ccProcessorId),
      processorRecordId: Number(this.data.processorRecordId),
      adxBookingId: Number(this.commReconEditRecordForm.value.adxBookingId.id)
    };
    this.commissionReconciliationService.addMapping(payload).subscribe({
      next: () => {
        this.snackBar.open('Booking mapped to commission record successfully', 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
        });
        this.isLoadingSpinner = false;
        this.dialogRef.close({ mapped: true });
      },
      error: err => {
        this.isLoadingSpinner = false;
        console.error(err);
      }
    });
  }
}
